<template>
  <v-dialog
    v-model="visible"
    transition="dialog-top-transition"
    class="pvt-param-config-dialog"
    content-class="pvt-param-config-dialog"
    max-width="1100"
    width="1100"
  >
    <v-card class="pvt-param-config-card">
      <div class="pvt-param-config-actions">
        <v-btn
          class="pvt-param-config-close-btn"
          flat
          icon
          size="x-small"
          @click="visible = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <button
          v-if="view"
          type="button"
          class="pvt-param-config-csv-link"
          @click="downloadCsv"
        >
          Download CSV
        </button>
      </div>
      <div v-if="loading" class="pvt-param-config-status">
        <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
        <p>Loading parameter configuration...</p>
      </div>
      <div v-else-if="errorMessage" class="pvt-param-config-status">
        <p>{{ errorMessage }}</p>
      </div>
      <div v-else-if="view" class="v2-mm-inline-detail">
        <div class="v2-mm-inline-detail-header">
          <span class="v2-mm-inline-detail-title">{{ view.title }}</span>
        </div>
        <div class="v2-mm-inline-columns">
          <div class="v2-mm-inline-col v2-mm-inline-col-builder">
            <div class="v2-mm-inline-col-label">Builder Configuration</div>
            <div class="v2-mm-inline-col-body">
              <div class="v2-mm-inline-block">
                <div v-if="view.builderRows.length" class="v2-mm-inline-fields">
                  <div
                    v-for="row in view.builderRows"
                    :key="row.label"
                    class="v2-mm-inline-field"
                  >
                    <span class="v2-mm-inline-field-label">{{ row.label }}</span>
                    <span class="v2-mm-inline-field-value">{{ row.value }}</span>
                  </div>
                </div>
                <div v-else class="v2-mm-inline-field">—</div>
              </div>
              <div class="v2-mm-inline-block">
                <div class="v2-mm-inline-block-title">Assembler Configuration</div>
                <div v-if="view.assemblerRows.length" class="v2-mm-inline-fields">
                  <div
                    v-for="row in view.assemblerRows"
                    :key="row.label"
                    class="v2-mm-inline-field"
                  >
                    <span class="v2-mm-inline-field-label">{{ row.label }}</span>
                    <span class="v2-mm-inline-field-value">{{ row.value }}</span>
                  </div>
                </div>
                <div v-else class="v2-mm-inline-field">—</div>
              </div>
            </div>
          </div>
          <div class="v2-mm-inline-col v2-mm-inline-col-lump">
            <div class="v2-mm-inline-col-label">Lump Parameters</div>
            <div class="v2-mm-inline-col-body">
              <div v-if="view.lumpEntries.length" class="v2-mm-inline-lump-list">
                <div
                  v-for="entry in view.lumpEntries"
                  :key="entry.lumpNum"
                  class="v2-mm-inline-lump-row"
                >
                  <span class="v2-mm-inline-lump-label">Lump {{ entry.lumpNum }}</span>
                  <div class="v2-mm-inline-lump-chips">
                    <span
                      v-for="component in entry.components"
                      :key="component"
                      class="v2-mm-inline-lump-chip"
                    >
                      {{ component }}
                    </span>
                    <span v-if="!entry.components.length" class="v2-muted">—</span>
                  </div>
                </div>
              </div>
              <div v-else class="v2-mm-inline-field">—</div>
            </div>
          </div>
          <div class="v2-mm-inline-col v2-mm-inline-col-runner">
            <div class="v2-mm-inline-col-label">Runner Configuration</div>
            <div class="v2-mm-inline-col-body">
              <template v-if="view.runnerSections.length">
                <div
                  v-for="(section, index) in view.runnerSections"
                  :key="section.title || index"
                  class="v2-mm-inline-block"
                >
                  <div v-if="section.title" class="v2-mm-inline-block-title">
                    {{ section.title }}
                  </div>
                  <div class="v2-mm-inline-fields">
                    <div
                      v-for="row in section.rows"
                      :key="row.label"
                      class="v2-mm-inline-field"
                    >
                      <span class="v2-mm-inline-field-label">{{ row.label }}</span>
                      <span class="v2-mm-inline-field-value">{{ row.value }}</span>
                    </div>
                  </div>
                </div>
              </template>
              <div v-else class="v2-mm-inline-field">—</div>
            </div>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/axiosInterceptor";
import { downloadParamCsv, loadPvtParamConfig } from "@/utils/pvtParamConfig";

export default {
  name: "PvtParamConfigModal",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    experimentType: {
      type: String,
      default: "cce",
    },
    sampleMeta: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      loading: false,
      errorMessage: "",
      view: null,
    };
  },
  computed: {
    visible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.loadConfig();
      }
    },
  },
  methods: {
    downloadCsv() {
      downloadParamCsv(this.view, this.experimentType);
    },
    async loadConfig() {
      this.loading = true;
      this.errorMessage = "";
      this.view = null;
      try {
        const result = await loadPvtParamConfig({
          apiGet: (url, config) => api.get(url, config),
          route: this.$route,
          sampleMeta: this.sampleMeta,
          experimentType: this.experimentType,
        });
        if (!result.ok) {
          this.errorMessage = result.message;
          return;
        }
        this.view = result.view;
      } catch (error) {
        this.errorMessage = error?.message || "Could not load parameter detail for this sample.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
